import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import type { Job } from '@vidmakr/bindings/types';
import { ArrowRightLeft, Check, Download, HardDrive, Pencil, RotateCcw, Trash2, XCircle, Cpu, Zap, Share2, Search, Send } from 'lucide-react';
import { ShareReviewModal } from '@/components/ReviewLinks';
import { SaveToSourceModal } from '@/components/StorageSources';
import { sendExport, useIntegrations } from '@/components/Integrations';
import { EXPORT_FORMATS, RENDER_STAGES, describeEncode, isGpuPool, normalizeEncode, renderStageOf, sortRows, type SortValue } from '@vidmakr/shared';
import { Badge, Button, EmptyState, Input, Modal, PageHeader, Progress, Select, cx } from '@/components/ui';
import { InfoRow, InfoSection, InfoTitle, InspectorPanel, InspectorToggle, SortTh, rowTone, tableCls } from '@/components/Browse';
import { useInspector, useSort } from '@/lib/browse';
import { useWorkerBuild, workerOutdated } from '@/components/WorkerUpdate';
import { GENERAL, useSpaces } from '@/lib/spaces';
import { reducers } from '@/lib/stdb';
import { attempt, toast } from '@/lib/toast';
import { openContextMenu } from '@/components/ContextMenu';
import { formatBytes, formatDate, timeAgo, isConvertJob, isExportJob } from '@/lib/format';

type JobRow = Job;

/** Gives an export a name of its own, in place of the project's. */
function RenameExportModal({ job, onClose }: { job: JobRow; onClose: () => void }) {
  const [title, setTitle] = useState(job.title);
  const [busy, setBusy] = useState(false);
  useEffect(() => setTitle(job.title), [job.id]);
  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    const ok = await attempt(() => reducers().renameJob({ jobId: job.id, title }));
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title="Rename export"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoFocus onKeyDown={(e) => e.key === 'Enter' && save()} />
    </Modal>
  );
}

/** Whether a running job is a plain export, which reports its progress in the shared step bands (see RENDER_STAGES); previews, stills and splits do not. */
function stagedKind(job: JobRow): boolean {
  if (job.kind !== 'render' || job.status !== 'running') return false;
  try {
    const s = JSON.parse(job.settings);
    return !s.preview && !s.still && !s.split;
  } catch {
    return false;
  }
}

/**
 * How a running export reports: 'steps' (the worker is current, or unknown to this viewer: only
 * admins see their organization's workers), 'old' (the worker is on an older build, which
 * reports in other ranges, so only the plain bar is honest), or 'plain' (not a step export, or
 * not known yet; nothing is assumed while the worker list loads, so the display does not flip).
 */
function useReporting(job: JobRow): 'steps' | 'old' | 'plain' {
  const [workers, ready] = useTable(tables.orgWorkers);
  const { latest, loaded } = useWorkerBuild();
  if (!stagedKind(job) || !ready || !loaded) return 'plain';
  return workerOutdated(workers.find((w) => w.id === job.workerId)?.version ?? '', latest) ? 'old' : 'steps';
}

/** "Rendering 42%": what a running export is doing right now. */
function stageText(job: JobRow): string {
  const { stage, fraction } = renderStageOf(job.progress);
  return stage.id === 'upload' && fraction < 0.02 ? 'Uploading…' : `${stage.label} ${Math.round(fraction * 100)}%`;
}

/** The steps of a running export, with the current one lit and its own percentage. */
export function ExportSteps({ job }: { job: JobRow }) {
  const reporting = useReporting(job);
  if (reporting === 'old') return <div className="mt-1 text-[11px] text-faint">This worker is on an older build: update it (GPU workers → Update and restart) for detailed progress.</div>;
  if (reporting !== 'steps') return null;
  const { index, fraction } = renderStageOf(job.progress);
  return (
    <ol className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]" data-export-steps>
      {RENDER_STAGES.map((st, i) => (
        <li key={st.id} className={cx('flex items-center gap-1', i < index ? 'text-ok' : i === index ? 'font-medium text-fg' : 'text-faint')} aria-current={i === index ? 'step' : undefined}>
          {i < index ? <Check size={11} /> : <span className={cx('h-1.5 w-1.5 rounded-full', i === index ? 'animate-pulse bg-accent' : 'bg-line-2')} />}
          {st.label}
          {i === index ? <span className="font-mono text-muted">{st.id === 'upload' && fraction < 0.02 ? '' : `${Math.round(fraction * 100)}%`}</span> : null}
        </li>
      ))}
    </ol>
  );
}

export function JobStatus({ job }: { job: JobRow }) {
  const staged = useReporting(job) === 'steps';
  switch (job.status) {
    case 'queued':
      return <Badge>Queued</Badge>;
    case 'running':
      return <Badge tone="accent">{staged ? stageText(job) : `${Math.round(job.progress * 100)}%`}</Badge>;
    case 'parts':
      // A split export while its parts render on several workers.
      return <Badge tone="accent">{Math.round(job.progress * 100)}% · parts</Badge>;
    case 'done':
      return <Badge tone="ok">Ready</Badge>;
    case 'failed':
      return <Badge tone="danger">Failed</Badge>;
    default:
      return <Badge>{job.status}</Badge>;
  }
}

export function describeSettings(raw: string): string {
  try {
    const s = JSON.parse(raw);
    if (s.encode) {
      // Output settings (formats.ts); conversions also name their source.
      const e = normalizeEncode(s.encode);
      const picture = s.width ? ` · ${s.width}×${s.height} · ${Math.round(s.fps * 1000) / 1000}fps` : '';
      return `${s.sourceName ? `From ${s.sourceName} · ` : ''}${describeEncode(e)}${e.videoCodec ? picture : ''}`;
    }
    const fmt = EXPORT_FORMATS.find((f) => f.id === s.format)?.label ?? s.format;
    return `${fmt} · ${s.width}×${s.height} · ${s.fps}fps · ${s.quality}`;
  } catch {
    return '';
  }
}

export function ExportRow({ job, canEdit, place }: { job: JobRow; canEdit: boolean; /** Where its project is, on the Exports page. */ place?: string }) {
  const navigate = useNavigate();
  const [sharing, setSharing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const download = `/api/exports/${job.id}/download`;
  // Conversions make a media file, not a video of a project: nothing to share or open.
  const convert = isConvertJob(job);
  const integrations = useIntegrations(job.orgId);
  return (
    <div
      className="flex items-center gap-2 px-3 py-3 sm:gap-3 sm:px-4"
      onContextMenu={(e) =>
        openContextMenu(e, [
          job.status === 'done' ? { label: 'Download', icon: <Download size={14} />, onClick: () => (window.location.href = download) } : null,
          job.status === 'done'
            ? { label: 'Copy download link', onClick: () => void navigator.clipboard.writeText(new URL(download, window.location.origin).toString()).then(() => toast.success('Link copied')) }
            : null,
          canEdit && job.status === 'done' && !convert ? { label: 'Share for review…', icon: <Share2 size={14} />, onClick: () => setSharing(true) } : null,
          canEdit && job.status === 'done' && !convert ? { label: 'Save to company storage…', icon: <HardDrive size={14} />, onClick: () => setSaving(true) } : null,
          integrations.length && job.status === 'done' ? { label: 'Send to…', icon: <Send size={14} />, onClick: () => sendExport(job, place) } : null,
          canEdit && job.kind !== 'render_part' ? { label: 'Rename…', icon: <Pencil size={14} />, onClick: () => setRenaming(true) } : null,
          job.projectId ? { label: 'Open project', onClick: () => navigate(`/p/${job.projectId}`) } : null,
          canEdit ? 'sep' : null,
          canEdit && (job.status === 'queued' || job.status === 'running' || job.status === 'parts') ? { label: 'Cancel', icon: <XCircle size={14} />, onClick: () => void attempt(() => reducers().cancelJob({ jobId: job.id })) } : null,
          canEdit && (job.status === 'failed' || job.status === 'canceled') ? { label: 'Retry', icon: <RotateCcw size={14} />, onClick: () => void attempt(() => reducers().retryJob({ jobId: job.id })) } : null,
          canEdit && job.status !== 'running' && job.status !== 'parts' ? { label: 'Delete', icon: <Trash2 size={14} />, danger: true, onClick: () => void attempt(() => reducers().deleteJob({ jobId: job.id })) } : null,
        ])
      }
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-panel-3 text-muted">
        {convert ? <ArrowRightLeft size={16} /> : isGpuPool(job.pool) ? <Zap size={16} /> : <Cpu size={16} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {job.projectId && !convert ? (
            <Link to={`/p/${job.projectId}`} className="truncate text-sm font-medium hover:underline" title={job.title}>
              {job.title}
            </Link>
          ) : (
            <span className="truncate text-sm font-medium" title={job.title}>{job.title}</span>
          )}
          <JobStatus job={job} />
        </div>
        <div
          className="truncate text-xs text-faint"
          title={[place, describeSettings(job.settings), job.createdByName, `Created ${formatDate(job.createdAt)}`, job.finishedAt ? `Finished ${formatDate(job.finishedAt)}` : ''].filter(Boolean).join(' · ')}
        >
          {place ? `${place} · ` : ''}
          {describeSettings(job.settings)} · {job.createdByName} · {timeAgo(job.createdAt)}
          {job.workerName ? ` · ${job.workerName}` : ''}
          {job.resultBytes ? ` · ${formatBytes(job.resultBytes)}` : ''}
        </div>
        {job.status === 'running' || job.status === 'parts' ? <Progress value={job.progress} className="mt-2" /> : null}
        <ExportSteps job={job} />
        {job.status === 'failed' ? <div className="mt-1 line-clamp-2 text-xs text-danger" title={job.error}>{job.error}</div> : null}
      </div>
      {canEdit && job.status === 'done' && !convert ? (
        <Button size="sm" variant="ghost" onClick={() => setSharing(true)} title="Share a link for review" data-share-export={job.id}>
          <Share2 size={14} /> <span className="hidden sm:inline">Share</span>
        </Button>
      ) : null}
      {sharing ? <ShareReviewModal job={job} open onClose={() => setSharing(false)} /> : null}
      {saving ? <SaveToSourceModal job={job} onClose={() => setSaving(false)} /> : null}
      {renaming ? <RenameExportModal job={job} onClose={() => setRenaming(false)} /> : null}
      {job.status === 'done' ? (
        <a href={`/api/exports/${job.id}/download`}>
          <Button size="sm" variant="primary">
            <Download size={14} /> <span className="hidden sm:inline">Download</span>
          </Button>
        </a>
      ) : null}
      {canEdit && (job.status === 'queued' || job.status === 'running' || job.status === 'parts') ? (
        <Button size="sm" variant="ghost" onClick={() => void attempt(() => reducers().cancelJob({ jobId: job.id }))}>
          <XCircle size={14} /> Cancel
        </Button>
      ) : null}
      {canEdit && (job.status === 'failed' || job.status === 'canceled') ? (
        <Button size="sm" variant="ghost" onClick={() => void attempt(() => reducers().retryJob({ jobId: job.id }))}>
          <RotateCcw size={14} /> Retry
        </Button>
      ) : null}
      {canEdit && job.status !== 'running' && job.status !== 'parts' ? (
        <Button size="icon-sm" variant="ghost" title="Delete" onClick={() => void attempt(() => reducers().deleteJob({ jobId: job.id }))}>
          <Trash2 size={14} />
        </Button>
      ) : null}
    </div>
  );
}


const SORT_KEYS = ['name', 'status', 'size', 'createdBy', 'created', 'rendered'] as const;
type SortKey = (typeof SORT_KEYS)[number];

/** Details of the selected export in the inspector panel. */
function ExportInspector({ job, place, canEdit }: { job?: JobRow; place: string; canEdit: boolean }) {
  const integrations = useIntegrations(job?.orgId);
  const [sharing, setSharing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState(false);
  if (!job) return <p className="text-xs text-faint">Select an export to see its details.</p>;
  const convert = isConvertJob(job);
  const active = job.status === 'queued' || job.status === 'running' || job.status === 'parts';
  const took = job.startedAt && job.finishedAt ? Number(job.finishedAt.microsSinceUnixEpoch - job.startedAt.microsSinceUnixEpoch) / 1e6 : 0;
  return (
    <>
      <InfoTitle title={job.title} subtitle={<JobStatus job={job} />} />
      <InfoSection title="Output">
        <InfoRow label="Settings">{describeSettings(job.settings)}</InfoRow>
        <InfoRow label="Size">{job.resultBytes ? formatBytes(job.resultBytes) : ''}</InfoRow>
        <InfoRow label="Pool">{isGpuPool(job.pool) ? 'GPU' : 'CPU'}</InfoRow>
      </InfoSection>
      <InfoSection title="When">
        <InfoRow label="Created">{formatDate(job.createdAt)}</InfoRow>
        <InfoRow label="Started">{job.startedAt ? formatDate(job.startedAt) : ''}</InfoRow>
        <InfoRow label="Rendered">{job.finishedAt ? formatDate(job.finishedAt) : ''}</InfoRow>
        <InfoRow label="Took">{took ? `${Math.max(1, Math.round(took))}s` : ''}</InfoRow>
      </InfoSection>
      <InfoSection title="Origin">
        <InfoRow label="By">{job.createdByName}</InfoRow>
        <InfoRow label="Worker">{job.workerName}</InfoRow>
        <InfoRow label="Project">
          {job.projectId && !convert ? (
            <Link to={`/p/${job.projectId}`} className="hover:underline">
              {place || 'Open'}
            </Link>
          ) : (
            place
          )}
        </InfoRow>
      </InfoSection>
      {job.status === 'failed' ? <p className="mb-3 text-xs text-danger">{job.error}</p> : null}
      <div className="flex flex-wrap gap-2">
        {job.status === 'done' ? (
          <a href={`/api/exports/${job.id}/download`}>
            <Button size="sm" variant="primary">
              <Download size={14} /> Download
            </Button>
          </a>
        ) : null}
        {canEdit && job.status === 'done' && !convert ? (
          <>
            <Button size="sm" variant="ghost" onClick={() => setSharing(true)}>
              <Share2 size={14} /> Share
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSaving(true)}>
              <HardDrive size={14} /> Save to storage
            </Button>
          </>
        ) : null}
        {integrations.length && job.status === 'done' ? (
          <Button size="sm" variant="ghost" onClick={() => sendExport(job, place)} data-send-export>
            <Send size={14} /> Send to…
          </Button>
        ) : null}
        {canEdit ? (
          <Button size="sm" variant="ghost" onClick={() => setRenaming(true)}>
            <Pencil size={14} /> Rename
          </Button>
        ) : null}
        {canEdit && active ? (
          <Button size="sm" variant="ghost" onClick={() => void attempt(() => reducers().cancelJob({ jobId: job.id }))}>
            <XCircle size={14} /> Cancel
          </Button>
        ) : null}
        {canEdit && (job.status === 'failed' || job.status === 'canceled') ? (
          <Button size="sm" variant="ghost" onClick={() => void attempt(() => reducers().retryJob({ jobId: job.id }))}>
            <RotateCcw size={14} /> Retry
          </Button>
        ) : null}
        {canEdit && job.status !== 'running' && job.status !== 'parts' ? (
          <Button size="sm" variant="ghost" onClick={() => void attempt(() => reducers().deleteJob({ jobId: job.id }))}>
            <Trash2 size={14} /> Delete
          </Button>
        ) : null}
      </div>
      {sharing ? <ShareReviewModal job={job} open onClose={() => setSharing(false)} /> : null}
      {renaming ? <RenameExportModal job={job} onClose={() => setRenaming(false)} /> : null}
      {saving ? <SaveToSourceModal job={job} onClose={() => setSaving(false)} /> : null}
    </>
  );
}

/**
 * Exports of every project the viewer can open (row-level security hides the
 * rest), filterable by space, project and name, as a sortable table with a
 * details panel. Actions follow the viewer's access to each project.
 */
export function ExportsPage() {
  const { orgId } = useParams();
  const sp = useSpaces(orgId);
  const [jobs] = useTable(tables.job.where((j) => j.orgId.eq(orgId ?? '')));
  const [projects] = useTable(tables.project.where((p) => p.orgId.eq(orgId ?? '')));
  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);
  const [space, setSpace] = useState('all');
  const [projectId, setProjectId] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [sort, onSort] = useSort<SortKey>('exports', SORT_KEYS, { key: 'created', dir: 'desc' });
  const inspector = useInspector();
  const renders = useMemo(() => [...jobs].filter((j) => isExportJob(j) || isConvertJob(j)), [jobs]);
  const spaceOf = (j: JobRow) => projectById.get(j.projectId)?.spaceId ?? '';
  const valueOf = (j: JobRow, k: SortKey): SortValue =>
    k === 'name' ? j.title : k === 'status' ? j.status : k === 'size' ? j.resultBytes : k === 'createdBy' ? j.createdByName : k === 'rendered' ? j.finishedAt?.microsSinceUnixEpoch : j.createdAt.microsSinceUnixEpoch;
  const shown = sortRows(
    renders.filter(
      (j) =>
        (space === 'all' || spaceOf(j) === (space === GENERAL ? '' : space)) &&
        (!projectId || j.projectId === projectId) &&
        (!query || j.title.toLowerCase().includes(query.toLowerCase())),
    ),
    (j) => valueOf(j, sort.key),
    sort.dir,
  );
  // Projects that have exports, in the chosen space.
  const withExports = [...new Set(renders.map((j) => j.projectId))]
    .map((id) => projectById.get(id))
    .filter((p): p is NonNullable<typeof p> => !!p && (space === 'all' || p.spaceId === (space === GENERAL ? '' : space)))
    .sort((a, b) => a.name.localeCompare(b.name));
  const placeOf = (j: JobRow) => {
    const p = projectById.get(j.projectId);
    if (!p) return '';
    return [p.spaceId ? sp.spaceById.get(p.spaceId)?.name : 'General', sp.folderById.get(p.folderId)?.name].filter(Boolean).join(' / ');
  };
  const selected = renders.find((j) => j.id === selectedId);
  const when = (ts?: JobRow['createdAt'] | null) => (ts ? <span title={formatDate(ts)}>{timeAgo(ts)}</span> : '—');
  return (
    <div className="flex items-start gap-6">
      <div className="min-w-0 flex-1">
        <PageHeader title="Exports" subtitle="Rendered videos of the projects you can open, and media conversions" />
        {renders.length === 0 ? (
          <EmptyState icon={<HardDrive size={32} />} title="No exports yet">
            Open a project and choose Export to render a video, or convert media with Convert… in the asset library.
          </EmptyState>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative w-64">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
                <Input className="pl-9" placeholder="Search exports" value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>
              <Select value={space} onChange={(e) => (setSpace(e.target.value), setProjectId(''))} aria-label="Space" data-exports-space>
                <option value="all">All spaces</option>
                <option value={GENERAL}>General</option>
                {sp.spaces.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="Project" data-exports-project>
                <option value="">All projects</option>
                {withExports.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <div className="ml-auto">
                <InspectorToggle open={inspector.open} onClick={inspector.toggle} />
              </div>
            </div>
            {shown.length === 0 ? (
              <EmptyState icon={<HardDrive size={32} />} title="No exports match" />
            ) : (
              <div className={tableCls.wrap}>
                <table className={tableCls.table} data-export-table>
                  <thead>
                    <tr className={tableCls.head}>
                      <SortTh k="name" sort={sort} onSort={onSort} first="asc" className="w-full">
                        Name
                      </SortTh>
                      <SortTh k="status" sort={sort} onSort={onSort} first="asc">
                        Status
                      </SortTh>
                      <SortTh k="size" sort={sort} onSort={onSort} align="right" className="hidden sm:table-cell">
                        Size
                      </SortTh>
                      <SortTh k="createdBy" sort={sort} onSort={onSort} first="asc" className="hidden lg:table-cell">
                        By
                      </SortTh>
                      <SortTh k="created" sort={sort} onSort={onSort} className="hidden md:table-cell">
                        Created
                      </SortTh>
                      <SortTh k="rendered" sort={sort} onSort={onSort}>
                        Rendered
                      </SortTh>
                      <th className="w-10 px-1" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((j) => (
                      <tr
                        key={j.id}
                        tabIndex={0}
                        data-export-row={j.id}
                        className={cx(tableCls.row, rowTone(j.id === selectedId, false))}
                        onClick={() => setSelectedId(j.id)}
                        onDoubleClick={() => !inspector.open && inspector.setOpen(true)}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedId(j.id)}
                      >
                        <td className="max-w-0 px-3 py-2">
                          <div className="truncate font-medium text-fg">{j.title}</div>
                          <div className="truncate text-xs text-faint">{[placeOf(j), describeSettings(j.settings)].filter(Boolean).join(' · ')}</div>
                          {j.status === 'running' || j.status === 'parts' ? <Progress value={j.progress} className="mt-1" /> : null}
                          <ExportSteps job={j} />
                        </td>
                        <td className={tableCls.cell}>
                          <JobStatus job={j} />
                        </td>
                        <td className={cx(tableCls.cell, 'hidden text-right sm:table-cell')}>{j.resultBytes ? formatBytes(j.resultBytes) : '—'}</td>
                        <td className={cx(tableCls.cell, 'hidden lg:table-cell')}>{j.createdByName || '—'}</td>
                        <td className={cx(tableCls.cell, 'hidden md:table-cell')}>{when(j.createdAt)}</td>
                        <td className={tableCls.cell}>{when(j.finishedAt)}</td>
                        <td className="px-1 py-1.5" onClick={(e) => e.stopPropagation()}>
                          {j.status === 'done' ? (
                            <a href={`/api/exports/${j.id}/download`} title="Download" aria-label="Download">
                              <Button size="icon-sm" variant="ghost" tabIndex={-1}>
                                <Download size={14} />
                              </Button>
                            </a>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
      <InspectorPanel open={inspector.open} wide={inspector.wide} onClose={() => inspector.setOpen(false)}>
        <ExportInspector key={selected?.id} job={selected} place={selected ? placeOf(selected) : ''} canEdit={selected ? sp.canIn(selected.scopeId, 'editor') : false} />
      </InspectorPanel>
    </div>
  );
}
